import * as React from 'react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { cn, customImageLoader } from '@restworld/utils-common';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { Heading } from '@restworld/ui-ds-base';

import type { JSX } from 'react';

export type PerformanceStatistic = {
  interested?: number;
  shortlisted?: number;
  views?: number;
  spontaneous_applications?: number;
  source?: string;
  order?: number;
};
export type PerformanceStatisticPlatformDataType = {
  icon?: JSX.Element;
  logo?: string;
  label?: string;
  description?: string;
  data?: PerformanceStatistic;
};
export interface TableProps {
  header: {
    label: string;
    order: number;
    key: keyof PerformanceStatistic;
  }[];
  data: PerformanceStatisticPlatformDataType[];
}
interface InfoRowProps {
  platform?: Partial<PerformanceStatisticPlatformDataType>;
  noBorder?: boolean;
  headerOrder: (keyof PerformanceStatistic)[];
}
export function EmployerPerformanceTable({ header, data }: TableProps) {
  const { t } = useTranslation('common');
  return (
    <StatsTableWrapper>
      <HeaderRowWrapper>
        <ColOneWrapper>
          <CellHeaderContent>{t('platform')}</CellHeaderContent>
        </ColOneWrapper>
        <ColTwoWrapper>
          {header?.map((v) => {
            return (
              <InfoCell key={v.key}>
                <CellHeaderContent>{v.label}</CellHeaderContent>
              </InfoCell>
            );
          })}
        </ColTwoWrapper>
      </HeaderRowWrapper>
      {data
        .sort((a, b) => {
          const shortlistedDiff = (b.data?.shortlisted || 0) - (a.data?.shortlisted || 0);
          if (shortlistedDiff !== 0) return shortlistedDiff;
          return (b.data?.interested || 0) - (a.data?.interested || 0);
        })
        .map((item, i) => {
          return (
            <InfoRow
              key={item.label}
              platform={item}
              noBorder={i === Object.keys(data).length - 1}
              headerOrder={header.map((v) => v.key)}
            />
          );
        })}
    </StatsTableWrapper>
  );
}

function InfoRow({ platform, noBorder, headerOrder }: InfoRowProps) {
  const { t: translateCommon } = useTranslation('common');
  const { logo, icon, data, description, label } = platform || {};

  return (
    <InfoRowWrapper noBorder={noBorder}>
      <ColOneWrapper>
        <div className="flex flex-col gap-2">
          <PlatformDetailsWrapper>
            {logo && (
              <LogoWrapper>
                <Image src={logo} alt="" fill loader={customImageLoader} className="object-cover object-center" />
              </LogoWrapper>
            )}
            {icon || <GlobeAltIcon className="h-5 w-5" />}
            <Heading as="span" size={6} weight="medium" color="neutral-light">
              {label}
            </Heading>
          </PlatformDetailsWrapper>
          <Heading as="span" size={6} color="neutral-light">
            {description}
          </Heading>
        </div>
      </ColOneWrapper>
      <ColTwoWrapper className="mt-4 sm:mt-0">
        {data
          ? headerOrder.map((v) => {
              return (
                <InfoCell key={v}>
                  <CellHeaderContent className="sm:hidden">{translateCommon(v)}</CellHeaderContent>
                  <Statistic>{data?.[v]}</Statistic>
                </InfoCell>
              );
            })
          : null}
      </ColTwoWrapper>
    </InfoRowWrapper>
  );
}
export default EmployerPerformanceTable;

const StatsTableWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('border border-neutral-40 rounded-2xl w-full', className)} {...props} />
);
const InfoCell = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p
    className={cn('flex-1  flex flex-col  sm:flex-row sm:justify-end sm:items-center sm:text-right', className)}
    {...props}
  />
);
const Statistic = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-sm font-semibold text-neutral-700', className)} {...props} />
);
const CellHeaderContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-200 uppercase font-semibold text-sm', className)} {...props} />
);
const ColOneWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full', className)} {...props} />
);
const ColTwoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full min-w-[310px] flex gap-4 items-start', className)} {...props} />
);
const PlatformDetailsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center gap-1 text-neutral-200', className)} {...props} />
);
const HeaderRowWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('border-b border-neutral-40 p-4 hidden sm:flex', className)} {...props} />
);
const InfoRowWrapper = ({ noBorder, className, ...props }: any) => (
  <div
    className={cn('border-neutral-40 py-6 px-4 sm:flex', noBorder ? 'border-none' : 'border-b', className)}
    {...props}
  />
);
const LogoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'border border-neutral-40 rounded relative w-9 h-9 min-w-[36px] flex items-center justify-center object-cover object-center overflow-hidden',
      className
    )}
    {...props}
  />
);
